import { t } from 'i18next';
import { memo, useCallback, useId, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { TypographyVariant } from '../../utils/theme';
import { getEmployerPensionContribution } from '../../utils/getEmployerPensionContribution';
import EmployeeSlider from '../employee-slider/employee-slider';
import EmployerSlider from '../employer-slider/employer-slider';
import Icon, { IconName } from '../icon/icon';
import { IconScale } from '../icon/types';
import { Tooltip } from '../tooltip/tooltip';
import Typography from '../typography/typography';

interface ISlider {
  minimumPercentage: number;
  maximumPercentage: number;
  label?: string;
}

export interface IOnChange {
  employeePercentage: number;
  employerPercentage: number;
}

export interface IPensionSliders {
  employeeProps: ISlider;
  employerProps: ISlider;
  employeePercentage: number;
  onChange: (props: IOnChange) => void;
  step?: number;
  hintText?: string;
  id?: string;
  isDisabled?: boolean;
}

export function PensionSliders({
  employeeProps,
  employerProps,
  step,
  onChange,
  employeePercentage,
  hintText,
  id = '',
  isDisabled = false,
}: IPensionSliders) {
  const { minimumPercentage, maximumPercentage, label } = employeeProps;
  const {
    minimumPercentage: employerMinPercentage,
    maximumPercentage: employerMaxPercentage,
    label: employerLabel,
  } = employerProps;
  const [showInvalidMessage, setShowInvalidMessage] = useState(false);

  const theme = useTheme();

  const defaultId = useId();
  const pensionSliderId = id || defaultId;

  const handleOnChange = useCallback(
    (employeePercentage: number) => {
      onChange({
        employeePercentage: employeePercentage,
        employerPercentage: getEmployerPensionContribution(
          employeePercentage,
          employerMaxPercentage,
          employerMinPercentage
        ),
      });
    },
    [onChange, employerMaxPercentage, employerMinPercentage]
  );

  const handleEmployeePercentageChange = useCallback(
    (newEmployeePercentage: number, fromInput?: boolean) => {
      const adjustedValue = step
        ? Math.round(newEmployeePercentage / step) * step
        : newEmployeePercentage;
      if (
        adjustedValue >= minimumPercentage &&
        adjustedValue <= maximumPercentage
      ) {
        setShowInvalidMessage(false);
        handleOnChange(adjustedValue);
      } else if (fromInput) {
        setShowInvalidMessage(true);
        handleOnChange(
          adjustedValue < minimumPercentage
            ? minimumPercentage
            : maximumPercentage
        );
      }
    },
    [minimumPercentage, maximumPercentage, handleOnChange, step]
  );

  const employerPercentageContribution = getEmployerPensionContribution(
    employeePercentage,
    employerMaxPercentage,
    employerMinPercentage
  );

  return (
    <SlidersContainer>
      {minimumPercentage > 0 && minimumPercentage !== maximumPercentage && (
        <TextContainer>
          <InlineTypography variant={TypographyVariant.bodyMd1}>
            {t('programme.pension.infoText')}
          </InlineTypography>
          <InlineTypography variant={TypographyVariant.bodyMd2}>
            &nbsp;{minimumPercentage}%
          </InlineTypography>
        </TextContainer>
      )}
      {hintText && (
        <TooltipContainer>
          <Tooltip
            tooltipText={hintText}
            id={`slider-tooltip-${pensionSliderId}`}
          >
            <Icon
              name={IconName.Info}
              color={theme.palette.neutral[80]}
              scale={IconScale.S}
            />
          </Tooltip>
        </TooltipContainer>
      )}
      <EmployeeSlider
        max={maximumPercentage}
        min={minimumPercentage}
        onChange={handleEmployeePercentageChange}
        value={employeePercentage}
        step={step || 0.5}
        isDisabled={isDisabled}
        label={label}
        showInvalidMessage={showInvalidMessage}
        aria-describedby={
          hintText ? `slider-tooltip-${pensionSliderId}` : undefined
        }
      />
      <EmployerSlider
        employeeMaxPercentageContribution={maximumPercentage}
        employerMaxPercentageContribution={employerMaxPercentage}
        value={employerPercentageContribution}
        step={step || 0.5}
        label={employerLabel}
        aria-describedby={
          hintText ? `slider-tooltip-${pensionSliderId}` : undefined
        }
      />
    </SlidersContainer>
  );
}

export default memo(PensionSliders);

const SlidersContainer = styled.div`
  position: relative;
`;

const InlineTypography = styled(Typography)`
  display: inline;
`;

const TextContainer = styled.div`
  ${({ theme }) => css`
    padding-bottom: ${theme.spacing(2.5)};
    margin-bottom: ${theme.spacing(3.5)};
    border-bottom: 1px dashed ${theme.palette.neutral[60]};
  `}
`;

const TooltipContainer = styled.div`
  position: absolute;
  top: -8px;
  right: -16px;
`;
